import { useMemo, memo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { UserButton } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Shield, AlertCircle, Loader2, AlertTriangle, Users, FileWarning, Bell } from "lucide-react";

// Memoized stat card component
const StatCard = memo(function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  iconClassName = "text-primary",
  valueClassName = "",
  borderClassName = ""
}: { 
  icon: React.ElementType;
  label: string;
  value: number;
  iconClassName?: string;
  valueClassName?: string;
  borderClassName?: string;
}) {
  return (
    <div className={`card-elegant p-6 ${borderClassName}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-5 h-5 ${iconClassName}`} />
        <p className="text-muted-foreground text-sm">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${valueClassName}`}>{value}</p>
    </div>
  );
});

// Memoized report item component
const ReportItem = memo(function ReportItem({ report }: { report: any }) {
  const priorityClass = useMemo(() => {
    if (report.priority === 'critical') return 'bg-destructive/20 text-destructive';
    if (report.priority === 'high') return 'bg-secondary/20 text-secondary';
    return 'bg-muted text-muted-foreground';
  }, [report.priority]);

  return (
    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
      <span className={`px-2 py-1 rounded text-xs font-medium ${priorityClass}`}>
        {report.priority}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{report.reportType.replace('_', ' ')}</p>
        <p className="text-xs text-muted-foreground truncate">{report.description}</p>
      </div>
      <Button variant="outline" size="sm">Review</Button>
    </div>
  );
});

// Memoized flagged ad item component
const FlaggedAdItem = memo(function FlaggedAdItem({ ad }: { ad: any }) {
  const statusClass = useMemo(() => 
    ad.status === 'flagged' ? 'bg-destructive/20 text-destructive' : 'bg-secondary/20 text-secondary',
    [ad.status]
  );

  return (
    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
        {ad.status}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{ad.title}</p>
        <p className="text-xs text-muted-foreground">
          Flags: {ad.moderationFlags?.join(', ') || 'Manual review'}
        </p>
      </div>
      <Button variant="outline" size="sm">Review</Button>
    </div>
  );
});

export default function AdminDashboard() {
  const currentUser = useQuery(api.auth.getCurrentUser);
  
  // Memoize query args to prevent unnecessary re-renders
  const reportsQueryArgs = useMemo(() => ({ limit: 5 }), []);
  const adsQueryArgs = useMemo(() => ({ limit: 5 }), []);
  
  const safetyStats = useQuery(api.safety.getSafetyStats);
  const pendingReports = useQuery(api.safety.getPendingReports, reportsQueryArgs);
  const flaggedAds = useQuery(api.safety.getFlaggedAds, adsQueryArgs);

  // Memoize reports and ads arrays
  const reportsList = useMemo(() => pendingReports ?? [], [pendingReports]);
  const flaggedAdsList = useMemo(() => flaggedAds ?? [], [flaggedAds]);

  // Loading state
  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Admin check - ProtectedRoute handles auth, but we need admin role
  if (currentUser?.role !== "admin") {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
          <div className="container flex items-center justify-between h-16">
            <Link href="/">
              <a className="text-2xl font-bold gradient-text">Listo</a>
            </Link>
          </div>
        </nav>
        <div className="container py-12 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h1 className="mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin dashboard.
          </p>
          <Button asChild>
            <Link href="/">
              <a>Back to Home</a>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <a className="text-2xl font-bold gradient-text">Listo</a>
          </Link>
          <div className="flex items-center gap-4">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-medium">Admin Panel</span>
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </nav>

      <div className="container py-8">
        <h1 className="mb-8">Admin Dashboard</h1>

        {/* Safety Alert Banner */}
        {safetyStats && safetyStats.criticalReports > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-8 flex items-center gap-4">
            <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0" />
            <div>
              <p className="font-semibold text-destructive">Critical Reports Pending</p>
              <p className="text-sm text-muted-foreground">
                {safetyStats.criticalReports} critical safety report(s) require immediate review.
              </p>
            </div>
            <Button variant="destructive" size="sm" className="ml-auto">
              Review Now
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Safety Stats Cards */}
          <StatCard
            icon={AlertTriangle}
            label="Critical Reports"
            value={safetyStats?.criticalReports || 0}
            iconClassName="text-destructive"
            valueClassName="text-destructive"
            borderClassName="border-destructive/20"
          />

          <StatCard
            icon={Bell}
            label="Pending Reports"
            value={safetyStats?.pendingReports || 0}
            iconClassName="text-secondary"
            valueClassName="text-secondary"
            borderClassName="border-secondary/20"
          />

          <StatCard
            icon={FileWarning}
            label="Flagged Ads"
            value={safetyStats?.flaggedAds || 0}
          />

          <StatCard
            icon={Users}
            label="Suspended Users"
            value={safetyStats?.suspendedUsers || 0}
            iconClassName="text-muted-foreground"
          />
        </div>

        {/* Safety Moderation Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            Safety & Moderation
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Reports */}
            <div className="card-elegant p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Recent Safety Reports
              </h3>
              {pendingReports && reportsList.length > 0 ? (
                <div className="space-y-3">
                  {reportsList.map((report: any) => (
                    <ReportItem key={report._id} report={report} />
                  ))}
                  <Button variant="outline" className="w-full">View All Reports</Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No pending reports.</p>
              )}
            </div>

            {/* Flagged Ads */}
            <div className="card-elegant p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-primary" />
                Flagged Ads
              </h3>
              {flaggedAds && flaggedAdsList.length > 0 ? (
                <div className="space-y-3">
                  {flaggedAdsList.map((ad: any) => (
                    <FlaggedAdItem key={ad._id} ad={ad} />
                  ))}
                  <Button variant="outline" className="w-full">View All Flagged</Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No flagged ads.</p>
              )}
            </div>
          </div>
        </div>

        {/* Management Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Ads Management */}
          <div className="card-elegant p-6">
            <h2 className="text-xl font-semibold mb-4">Ads Management</h2>
            <p className="text-muted-foreground mb-4">
              Review and moderate classified ads posted on the platform.
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                Pending Approval ({safetyStats?.pendingApproval || 0})
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Active Ads
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Flagged Content ({safetyStats?.flaggedAds || 0})
              </Button>
            </div>
          </div>

          {/* Users Management */}
          <div className="card-elegant p-6">
            <h2 className="text-xl font-semibold mb-4">Users Management</h2>
            <p className="text-muted-foreground mb-4">
              Manage user accounts and roles on the platform.
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                All Users
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Verified Users
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Suspended Users ({safetyStats?.suspendedUsers || 0})
              </Button>
            </div>
          </div>

          {/* Keywords Management */}
          <div className="card-elegant p-6">
            <h2 className="text-xl font-semibold mb-4">Content Moderation</h2>
            <p className="text-muted-foreground mb-4">
              Manage blocked keywords and content filters.
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                Blocked Keywords
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Add Keyword Filter
              </Button>
              <Button variant="outline" className="w-full justify-start">
                View Audit Log
              </Button>
            </div>
          </div>

          {/* Law Enforcement */}
          <div className="card-elegant p-6 border-destructive/20">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-destructive" />
              Law Enforcement
            </h2>
            <p className="text-muted-foreground mb-4">
              Tools for handling escalated reports and law enforcement requests.
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start text-destructive">
                Escalated Reports
              </Button>
              <Button variant="outline" className="w-full justify-start">
                Export Evidence
              </Button>
              <Button variant="outline" className="w-full justify-start">
                NCMEC Report
              </Button>
            </div>
          </div>
        </div>

        {/* Important Contacts */}
        <div className="mt-12 p-6 bg-primary/5 border border-primary/20 rounded-lg">
          <h3 className="font-semibold mb-4">Emergency Contacts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium">Human Trafficking Hotline</p>
              <p className="text-muted-foreground">1-888-373-7888</p>
            </div>
            <div>
              <p className="font-medium">NCMEC CyberTipline</p>
              <p className="text-muted-foreground">1-800-843-5678</p>
            </div>
            <div>
              <p className="font-medium">FBI IC3</p>
              <p className="text-muted-foreground">ic3.gov</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
